import { useEffect, useRef } from "react";
import { LngLatBounds, Map as MLMap, NavigationControl, Popup } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MESHCORE_SNR_COLOR_HEX } from "../lib/definitions/protocols";
import { buildOsmStyle, MAP_CONTAINER_BACKGROUND } from "../lib/mapStyle";
import { navigate } from "../lib/router";
import { segText, type MapPlan } from "../lib/traces";

// A trace's combined route on the same MapLibre base map as the Live Map and the Path Inspector. Each placed hop is a dot and
// each link a line coloured by its SNR with the same thresholds as the dots; lib/traces.ts (mapPlan) decides what is drawn.
// Rebuilt for each route and theme (the parent keys it).

const NO_SIGNAL = "#898781";

function cssVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

export function TraceRouteMap({ plan, theme, height }: { plan: MapPlan; theme: "light" | "dark"; height: number }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const lines = {
      type: "FeatureCollection",
      features: plan.segments.map((s) => ({
        type: "Feature",
        properties: { color: s.color ? MESHCORE_SNR_COLOR_HEX[s.color] : NO_SIGNAL, text: segText(s) },
        geometry: { type: "LineString", coordinates: [[s.a.lon, s.a.lat], [s.b.lon, s.b.lat]] },
      })),
    };
    const dots = {
      type: "FeatureCollection",
      features: plan.points.map((p) => ({
        type: "Feature",
        properties: { node: p.id, name: p.name || p.id.slice(0, 8) },
        geometry: { type: "Point", coordinates: [p.lon, p.lat] },
      })),
    };
    const map = new MLMap({
      container: el,
      style: buildOsmStyle(theme),
      center: [-114, 44],
      zoom: 5,
      attributionControl: { compact: true },
      canvasContextAttributes: { preserveDrawingBuffer: true },
    });
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    const popup = new Popup({ closeButton: false, closeOnClick: false, maxWidth: "260px" });
    const init = () => {
      const ink = cssVar(el, "--ink", "#111");
      const surface = cssVar(el, "--surface", "#fff");
      map.addSource("tm-lines", { type: "geojson", data: lines as never });
      map.addSource("tm-dots", { type: "geojson", data: dots as never });
      map.addLayer({ id: "tm-casing", type: "line", source: "tm-lines", layout: { "line-cap": "round" }, paint: { "line-color": surface, "line-width": 7, "line-opacity": 0.9 } });
      map.addLayer({ id: "tm-lines", type: "line", source: "tm-lines", layout: { "line-cap": "round" }, paint: { "line-color": ["get", "color"], "line-width": 4 } });
      map.addLayer({
        id: "tm-dots", type: "circle", source: "tm-dots",
        paint: { "circle-color": surface, "circle-radius": 6, "circle-stroke-width": 2.5, "circle-stroke-color": ink },
      });
      if (plan.points.length === 1) {
        map.jumpTo({ center: [plan.points[0].lon, plan.points[0].lat], zoom: 9 });
      } else if (plan.points.length > 1) {
        const b = new LngLatBounds();
        for (const p of plan.points) b.extend([p.lon, p.lat]);
        map.fitBounds(b, { padding: 40, maxZoom: 10, duration: 0 });
      }
    };
    if (map.isStyleLoaded()) init();
    else map.once("style.load", init);
    const hover = (layer: string, prop: string) => {
      map.on("mousemove", layer, (e) => {
        map.getCanvas().style.cursor = "pointer";
        const text = e.features?.[0]?.properties?.[prop];
        if (text) popup.setLngLat(e.lngLat).setText(String(text)).addTo(map);
      });
      map.on("mouseleave", layer, () => {
        map.getCanvas().style.cursor = "";
        popup.remove();
      });
    };
    hover("tm-lines", "text");
    hover("tm-dots", "name");
    map.on("click", "tm-dots", (e) => {
      const id = e.features?.[0]?.properties?.node;
      if (id) navigate(`#/nodes/${encodeURIComponent(String(id))}`);
    });
    return () => {
      popup.remove();
      map.remove();
    };
  }, [plan, theme]);

  return (
    <div className="pi-map tr-map" style={{ height, background: MAP_CONTAINER_BACKGROUND[theme] }}>
      <div ref={ref} className="pi-map-canvas" />
    </div>
  );
}
